import { Modal, message } from 'antd';
import axiosApiInstance from '../../../api/axiosClient';

type DuplicateConflict = {
    code?: string;
    existing?: {key_name: string; spotify_id?: string}[];
    error?: string;
};

function normalizeSpotify(id?: string | null): string {
    return (id ?? '').trim();
}

function findCaseInsensitiveMatches(
    keyName: string,
    artists: API.MusicDistribution.Artist[],
): API.MusicDistribution.Artist[] {
    const lower = keyName.trim().toLowerCase();
    return artists.filter((a) => a.key_name.trim().toLowerCase() === lower);
}

async function postArtist(values: API.MusicDistribution.Artist, confirmDuplicateName: boolean) {
    await axiosApiInstance.post('/api/products/artists/', values, {
        params: confirmDuplicateName ? {confirm_duplicate_name: 'true'} : undefined,
    });
}

/**
 * Creates an artist, confirming when the same name already exists without a
 * distinct Spotify ID to disambiguate.
 */
export async function createArtistWithDuplicateCheck(
    values: API.MusicDistribution.Artist,
    artists: API.MusicDistribution.Artist[],
): Promise<void> {
    const keyName = values.key_name?.trim();
    if (!keyName) {
        throw new Error('Key name is required');
    }

    const matches = findCaseInsensitiveMatches(keyName, artists);
    if (matches.some((m) => m.key_name === keyName)) {
        message.error(`Artist "${keyName}" already exists.`);
        throw new Error('duplicate exact key');
    }

    const newSpotify = normalizeSpotify(values.spotify_id);
    if (matches.length > 0 && newSpotify) {
        if (matches.some((m) => normalizeSpotify(m.spotify_id) === newSpotify)) {
            message.error('This Spotify ID is already linked to an artist with the same name.');
            throw new Error('duplicate spotify');
        }
        await postArtist(values, false);
        return;
    }

    if (matches.length === 0) {
        try {
            await postArtist(values, false);
            return;
        } catch (err: unknown) {
            const data = (err as {response?: {data?: DuplicateConflict}})?.response?.data;
            if (data?.code === 'duplicate_name_needs_confirm') {
                await confirmAndCreate(values, data);
                return;
            }
            throw err;
        }
    }

    await confirmAndCreate(values, {
        existing: matches.map((m) => ({
            key_name: m.key_name,
            spotify_id: m.spotify_id,
        })),
    });
}

function confirmAndCreate(
    values: API.MusicDistribution.Artist,
    hint: DuplicateConflict,
): Promise<void> {
    const existing = hint.existing ?? [];
    const lines = existing
        .map((e) =>
            e.spotify_id
                ? `${e.key_name} — Spotify ${e.spotify_id}`
                : `${e.key_name} — no Spotify ID`,
        )
        .join('\n');

    return new Promise((resolve, reject) => {
        Modal.confirm({
            title: 'Artist name already exists',
            content: (
                <div>
                    <p>
                        An artist with the same name is already in the catalog. Create{' '}
                        <strong>{values.key_name}</strong> anyway?
                    </p>
                    {lines ? <pre style={{whiteSpace: 'pre-wrap', marginBottom: 0}}>{lines}</pre> : null}
                </div>
            ),
            okText: 'Create',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await postArtist(values, true);
                    resolve();
                } catch (err) {
                    message.error('Could not create artist');
                    reject(err);
                }
            },
            onCancel: () => reject(new Error('cancelled')),
        });
    });
}
